import type {
  CreateProductRequest,
  CreateVariantRequest,
  Product,
  ProductDraftPayload,
  ProductType,
  ProductionMethod,
  VariantFilamentRequest,
} from '@/types/api';

export type ProductFormStep =
  | 'basics'
  | 'itemType'
  | 'collection'
  | 'productionMethods'
  | 'materials'
  | 'variants'
  | 'pricing';

export const WIZARD_STEPS: { id: ProductFormStep; label: string }[] = [
  { id: 'basics', label: 'Name & Basics' },
  { id: 'itemType', label: 'Product Type' },
  { id: 'collection', label: 'Collection & Design' },
  { id: 'productionMethods', label: 'Production Method' },
  { id: 'materials', label: 'Materials' },
  { id: 'variants', label: 'Variants' },
  { id: 'pricing', label: 'Pricing & Inventory' },
];

const VALID_WIZARD_STEP_IDS = new Set<ProductFormStep>(WIZARD_STEPS.map((s) => s.id));

export function normalizeWizardStep(step: string | null | undefined): ProductFormStep {
  if (step && VALID_WIZARD_STEP_IDS.has(step as ProductFormStep)) {
    return step as ProductFormStep;
  }
  return 'basics';
}

export interface VariantFilamentFormRow {
  id: string;
  supplyId: string;
  supplyName?: string;
  grams: string;
  extruderSlot: string;
  sortOrder: number;
  notes: string;
}

export interface VariantSupplyFormRow {
  id: string;
  supplyId: string;
  supplyName?: string;
  quantityRequired: string;
  unitOfMeasure: string;
  notes: string;
}

export interface VariantFormState {
  id: string;
  name: string;
  variantSku: string;
  variantFilaments: VariantFilamentFormRow[];
  variantSupplies: VariantSupplyFormRow[];
  filamentId: string | null;
  modelExternalUrl: string;
  designExternalUrl: string;
  designNotes: string;
  imageUrl: string | null;
  notes: string;
  initialQuantity: string;
  locationId: string;
  binLocation: string;
  binNumber: string;
}

export interface ProductSupplyFormState {
  id: string;
  supplyId: string;
  supplyName: string;
  quantityRequired: string;
  unitOfMeasure: string;
  notes: string;
}

export interface ProductFormState {
  itemTypeId: string | null;
  collectionId: string | null;
  categoryIds: string[];
  productionMethodIds: string[];
  productDesign: string;

  sku: string;
  name: string;
  description: string;
  category: string;

  productType: ProductType;

  basePrice: string;
  cost: string;
  dimensionHeight: string;
  dimensionWidth: string;
  dimensionDepth: string;

  material: string;
  upcCode: string;
  upcCode2: string;
  systemUpc: string;

  primaryImageUrl: string | null;
  imageUrls: string[];
  designExternalUrl: string;
  designFilePath: string;
  designFileType: string;
  designFileName: string;
  modelFilePath: string;
  modelExternalUrl: string;
  craftMetadata: Record<string, string>;
  blankSupplyId: string;

  supplies: ProductSupplyFormState[];
  variants: VariantFormState[];
  filamentIds: string[];

  wordpressProductId: number | null;
}

export interface ProductFormErrors {
  itemTypeId?: string;
  name?: string;
  basePrice?: string;
  variants?: string;
  submit?: string;
}

export type ProductFormMode = 'wizard' | 'single';

export type DraftSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

const CODE_TO_PRODUCT_TYPE: Record<string, ProductType> = {
  THREE_D_PRINTED: 'THREE_D_PRINTED',
  UV_PRINTED: 'UV_PRINTED',
  UV_TRANSFER: 'UV_PRINTED',
  LASER_ENGRAVED: 'LASER_ENGRAVED',
  LASER_CUTTING: 'LASER_ENGRAVED',
  SUBLIMATION: 'SUBLIMATION',
  EMBROIDERY: 'EMBROIDERY',
  DTF: 'APPAREL',
  VINYL_CUTTING: 'VINYL_CUTTING',
  APPAREL: 'APPAREL',
  BUTTON: 'BUTTON',
  STANDARD: 'STANDARD',
  PRINT_ON_DEMAND: 'PRINT_ON_DEMAND',
};

function createDefaultVariant(id = '1'): VariantFormState {
  return {
    id,
    name: 'Standard',
    variantSku: '',
    variantFilaments: [],
    variantSupplies: [],
    filamentId: null,
    modelExternalUrl: '',
    designExternalUrl: '',
    designNotes: '',
    imageUrl: null,
    notes: '',
    initialQuantity: '0',
    locationId: '',
    binLocation: '',
    binNumber: '',
  };
}

export function createEmptyProductFormState(): ProductFormState {
  return {
    itemTypeId: null,
    collectionId: null,
    categoryIds: [],
    productionMethodIds: [],
    productDesign: '',
    sku: '',
    name: '',
    description: '',
    category: '',
    productType: 'STANDARD',
    basePrice: '',
    cost: '',
    dimensionHeight: '',
    dimensionWidth: '',
    dimensionDepth: '',
    material: '',
    upcCode: '',
    upcCode2: '',
    systemUpc: '',
    primaryImageUrl: null,
    imageUrls: [],
    designExternalUrl: '',
    designFilePath: '',
    designFileType: '',
    designFileName: '',
    modelFilePath: '',
    modelExternalUrl: '',
    craftMetadata: {},
    blankSupplyId: '',
    supplies: [],
    variants: [createDefaultVariant()],
    filamentIds: [],
    wordpressProductId: null,
  };
}

export function resolveProductType(
  state: ProductFormState,
  methods: ProductionMethod[]
): ProductType {
  const selected = methods.filter((m) => state.productionMethodIds.includes(m.id));
  if (selected.length === 0) return state.productType;
  const primary = selected[0];
  return CODE_TO_PRODUCT_TYPE[primary.code] ?? state.productType;
}

export function stateToDraftPayload(
  state: ProductFormState,
  methods: ProductionMethod[] = []
): ProductDraftPayload {
  const request = stateToCreateRequest(state, methods);
  return {
    ...request,
    itemTypeId: state.itemTypeId,
    collectionId: state.collectionId,
    productionMethodIds: state.productionMethodIds,
    productDesign: state.productDesign.trim() || null,
    selectedCategoryIds: state.categoryIds,
  };
}

export function draftPayloadToState(payload: ProductDraftPayload): ProductFormState {
  const base = createEmptyProductFormState();
  const categoryIds = payload.categoryIds ?? payload.selectedCategoryIds ?? [];

  return {
    ...base,
    itemTypeId: payload.itemTypeId ?? null,
    collectionId: payload.collectionId ?? null,
    categoryIds,
    productionMethodIds: payload.productionMethodIds ?? [],
    productDesign: payload.productDesign ?? '',
    sku: payload.sku ?? '',
    name: payload.name ?? '',
    description: payload.description ?? '',
    category: payload.category ?? '',
    productType: payload.productType ?? 'STANDARD',
    basePrice: payload.basePrice != null ? String(payload.basePrice) : '',
    cost: payload.cost != null ? String(payload.cost) : '',
    dimensionHeight:
      payload.dimensionHeight != null ? String(payload.dimensionHeight) : '',
    dimensionWidth:
      payload.dimensionWidth != null ? String(payload.dimensionWidth) : '',
    dimensionDepth:
      payload.dimensionDepth != null ? String(payload.dimensionDepth) : '',
    material: payload.material ?? '',
    upcCode: payload.upcCode ?? '',
    upcCode2: payload.upcCode2 ?? '',
    systemUpc: payload.systemUpc ?? '',
    primaryImageUrl: payload.primaryImageUrl ?? null,
    imageUrls: payload.imageUrls ?? [],
    designExternalUrl: payload.designExternalUrl ?? '',
    designFilePath: payload.designFilePath ?? '',
    designFileType: payload.designFileType ?? '',
    designFileName: payload.designFileName ?? '',
    modelFilePath: payload.modelFilePath ?? '',
    modelExternalUrl: payload.modelExternalUrl ?? '',
    craftMetadata: payload.craftMetadata
      ? (() => {
          try {
            return JSON.parse(payload.craftMetadata) as Record<string, string>;
          } catch {
            return {};
          }
        })()
      : {},
    blankSupplyId: payload.blankSupplyId ?? '',
    filamentIds: payload.filamentIds ?? [],
    supplies:
      payload.supplies?.map((ps, idx) => ({
        id: `supply-${idx}`,
        supplyId: ps.supplyId,
        supplyName: '',
        quantityRequired: String(ps.quantityRequired ?? 1),
        unitOfMeasure: ps.unitOfMeasure ?? '',
        notes: ps.notes ?? '',
      })) ?? [],
    variants:
      payload.variants && payload.variants.length > 0
        ? payload.variants.map((v, idx) => ({
            id: `variant-${idx}`,
            name: v.name ?? 'Standard',
            variantSku: v.variantSku ?? '',
            variantFilaments:
              v.variantFilaments?.map((vf, vfIdx) => ({
                id: `vf-${idx}-${vfIdx}`,
                supplyId: vf.supplyId,
                grams: String(vf.grams ?? 1),
                extruderSlot:
                  vf.extruderSlot != null ? String(vf.extruderSlot) : '',
                sortOrder: vf.sortOrder ?? vfIdx,
                notes: vf.notes ?? '',
              })) ?? [],
            variantSupplies:
              v.variantSupplies?.map((vs, vsIdx) => ({
                id: `vs-${idx}-${vsIdx}`,
                supplyId: vs.supplyId,
                quantityRequired: String(vs.quantityRequired ?? 1),
                unitOfMeasure: vs.unitOfMeasure ?? 'piece',
                notes: vs.notes ?? '',
              })) ?? [],
            filamentId: v.filamentId ?? null,
            modelExternalUrl: v.modelExternalUrl ?? '',
            designExternalUrl: v.designExternalUrl ?? '',
            designNotes: v.designNotes ?? '',
            imageUrl: v.imageUrl ?? null,
            notes: v.notes ?? '',
            initialQuantity:
              v.initialQuantity != null ? String(v.initialQuantity) : '0',
            locationId: v.locationId ?? '',
            binLocation: v.binLocation ?? '',
            binNumber: v.binNumber ?? '',
          }))
        : [createDefaultVariant()],
  };
}

export function productToFormState(
  product: Product,
  options?: { duplicate?: boolean }
): ProductFormState {
  const duplicate = options?.duplicate ?? false;
  const state = createEmptyProductFormState();

  state.sku = duplicate ? '' : product.sku || '';
  state.name = duplicate ? `${product.name} (Copy)` : product.name || '';
  state.description = product.description || '';
  state.category = product.category || '';
  state.basePrice = product.basePrice?.toString() || '';
  state.cost = product.cost?.toString() || '';
  state.dimensionHeight = product.dimensionHeight?.toString() || '';
  state.dimensionWidth = product.dimensionWidth?.toString() || '';
  state.dimensionDepth = product.dimensionDepth?.toString() || '';
  state.productType = product.productType || 'STANDARD';
  state.primaryImageUrl = product.primaryImageUrl || null;
  state.imageUrls = product.imageUrls || [];
  state.designExternalUrl = product.designExternalUrl || '';
  state.designFilePath = product.designFilePath || '';
  state.designFileType = product.designFileType || '';
  state.designFileName = product.designFileName || '';
  state.modelFilePath = product.modelFilePath || '';
  state.modelExternalUrl = product.modelExternalUrl || '';
  state.material = product.material || '';
  state.upcCode = duplicate ? '' : product.upcCode || '';
  state.upcCode2 = duplicate ? '' : product.upcCode2 || '';
  state.systemUpc = duplicate ? '' : product.systemUpc || '';

  if (product.categories && product.categories.length > 0) {
    state.categoryIds = product.categories.map((c) => c.id);
  }

  if (product.productFilaments) {
    state.filamentIds = product.productFilaments.map((pf) => pf.filamentId);
  }

  try {
    state.craftMetadata = product.craftMetadata
      ? JSON.parse(product.craftMetadata)
      : {};
  } catch {
    state.craftMetadata = {};
  }

  const extended = product as Product & {
    itemTypeId?: string | null;
    collectionId?: string | null;
    productDesign?: string | null;
    productionMethodIds?: string[];
  };

  if (!duplicate) {
    state.itemTypeId = extended.itemTypeId ?? null;
    state.collectionId = extended.collectionId ?? null;
    state.productDesign = extended.productDesign ?? '';
    state.productionMethodIds = extended.productionMethodIds ?? [];
  }

  if (!duplicate && product.variants?.length) {
    state.variants = product.variants.map((v, idx) => ({
      id: v.id || `variant-${idx}`,
      name: v.name || v.displayName || 'Standard',
      variantSku: v.variantSku || '',
      variantFilaments:
        v.variantFilaments?.map((vf, vfIdx) => ({
          id: vf.id || `vf-${idx}-${vfIdx}`,
          supplyId: vf.filament?.id ?? '',
          supplyName: vf.filament?.name ?? '',
          grams: String(vf.quantityGrams ?? 1),
          extruderSlot: '',
          sortOrder: vf.sortOrder ?? vfIdx,
          notes: vf.notes ?? '',
        })) ?? [],
      variantSupplies: [],
      filamentId: v.filament?.id ?? null,
      modelExternalUrl: v.modelExternalUrl || '',
      designExternalUrl: v.designExternalUrl || '',
      designNotes: v.designNotes || '',
      imageUrl: v.imageUrl || null,
      notes: v.notes || '',
      initialQuantity: '0',
      locationId: '',
      binLocation: '',
      binNumber: '',
    }));
  }

  return state;
}

export function stateToCreateRequest(
  state: ProductFormState,
  methods: ProductionMethod[]
): CreateProductRequest {
  const productType = resolveProductType(state, methods);
  const craftMeta =
    Object.keys(state.craftMetadata).length > 0
      ? JSON.stringify(state.craftMetadata)
      : null;

  const variants: CreateVariantRequest[] = state.variants.map((v) => {
    const variantFilaments: VariantFilamentRequest[] | undefined =
      v.variantFilaments.length > 0
        ? v.variantFilaments.map((vf, idx) => ({
            supplyId: vf.supplyId,
            grams: parseFloat(vf.grams) || 1,
            sortOrder: vf.sortOrder ?? idx,
            notes: vf.notes.trim() || undefined,
            extruderSlot: vf.extruderSlot ? parseInt(vf.extruderSlot, 10) : null,
          }))
        : undefined;

    const variantSupplies =
      v.variantSupplies.length > 0
        ? v.variantSupplies.map((vs) => ({
            supplyId: vs.supplyId,
            quantityRequired: parseFloat(vs.quantityRequired) || 1,
            unitOfMeasure: vs.unitOfMeasure || undefined,
            notes: vs.notes.trim() || undefined,
          }))
        : undefined;

    return {
      variantSku: v.variantSku.trim() || undefined,
      name: v.name.trim() || 'Standard',
      filamentId: v.filamentId || null,
      variantFilaments,
      variantSupplies,
      modelExternalUrl:
        productType === 'THREE_D_PRINTED' ? v.modelExternalUrl.trim() || null : null,
      designExternalUrl:
        productType !== 'THREE_D_PRINTED' ? v.designExternalUrl.trim() || null : null,
      designNotes: v.designNotes.trim() || undefined,
      imageUrl: v.imageUrl || undefined,
      notes: v.notes.trim() || undefined,
      initialQuantity: parseInt(v.initialQuantity, 10) || 0,
      locationId: v.locationId.trim() || null,
      binLocation: v.binLocation.trim() || null,
      binNumber: v.binNumber.trim() || null,
    };
  });

  return {
    sku: state.sku.trim() || undefined,
    name: state.name.trim(),
    description: state.description.trim(),
    category: state.category.trim(),
    basePrice: parseFloat(state.basePrice) || 0,
    cost: state.cost ? parseFloat(state.cost) : 0,
    dimensionHeight: state.dimensionHeight ? parseFloat(state.dimensionHeight) : null,
    dimensionWidth: state.dimensionWidth ? parseFloat(state.dimensionWidth) : null,
    dimensionDepth: state.dimensionDepth ? parseFloat(state.dimensionDepth) : null,
    productType,
    craftType: productType,
    tracksInventory: productType !== 'PRINT_ON_DEMAND',
    is3dPrinted: productType === 'THREE_D_PRINTED',
    wordpressProductId: null,
    filamentIds:
      productType === 'THREE_D_PRINTED' && state.filamentIds.length > 0
        ? state.filamentIds
        : undefined,
    primaryImageUrl: state.primaryImageUrl || undefined,
    imageUrls: state.imageUrls.length > 0 ? state.imageUrls : undefined,
    designExternalUrl: state.designExternalUrl.trim() || null,
    designFilePath: state.designFilePath.trim() || null,
    designFileType: state.designFileType.trim() || null,
    designFileName: state.designFileName.trim() || null,
    modelFilePath: state.modelFilePath.trim() || null,
    modelExternalUrl: state.modelExternalUrl.trim() || null,
    craftMetadata: craftMeta,
    categoryIds: state.categoryIds.length > 0 ? state.categoryIds : undefined,
    blankSupplyId: state.blankSupplyId || undefined,
    supplies:
      state.supplies.length > 0
        ? state.supplies.map((ps) => ({
            supplyId: ps.supplyId,
            quantityRequired: parseFloat(ps.quantityRequired) || 1,
            unitOfMeasure: ps.unitOfMeasure || undefined,
            notes: ps.notes || undefined,
          }))
        : undefined,
    material: state.material.trim() || null,
    upcCode: state.upcCode.trim() || null,
    upcCode2: state.upcCode2.trim() || null,
    systemUpc: state.systemUpc.trim() || null,
    productDesign: state.productDesign.trim() || null,
    itemTypeId: state.itemTypeId,
    collectionId: state.collectionId,
    productionMethodIds:
      state.productionMethodIds.length > 0 ? state.productionMethodIds : undefined,
    variants,
  };
}

export function validateVariantNames(variants: VariantFormState[]): string | undefined {
  const names = variants.map((v) => v.name.trim().toLowerCase()).filter(Boolean);
  const duplicates = names.filter((name, idx) => names.indexOf(name) !== idx);
  if (duplicates.length > 0) {
    return `Duplicate variant names: ${[...new Set(duplicates)].join(', ')}`;
  }
  return undefined;
}

export function generateSkuPreview(productName: string): string {
  if (!productName.trim()) return '';
  return productName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/\s+/)
    .map((word) => word.replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toUpperCase())
    .filter(Boolean)
    .join('-')
    .slice(0, 20);
}
